import {
    ArrowDown,
    ArrowUp,
    Download,
    Image as ImageIcon,
    MousePointer2,
    Square,
    Trash2,
    Upload,
} from "lucide-react";
import { usePixelStretch } from "./PixelStretchContext";
import { CheckboxField } from "../reusable/CheckboxField";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { PresetSelector } from "../reusable/PresetSelector";
import { SendSvgToToolMenu } from "../reusable/SendSvgToToolMenu";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";
import { UndoRedoControls } from "../reusable/UndoRedoControls";

export function PixelStretchControls() {
    const {
        imageName,
        imageLoadError,
        nodes,
        selectedNodeId,
        toolMode,
        artboardWidth,
        artboardHeight,
        backgroundColor,
        connectorMaxSamples,
        showSourceImage,
        imageInputRef,
        handleImageLoad,
        setToolMode,
        setSelectedNodeId,
        setArtboardWidth,
        setArtboardHeight,
        setBackgroundColor,
        setConnectorMaxSamples,
        setShowSourceImage,
        removeNode,
        moveNodeUp,
        moveNodeDown,
        clearNodes,
        canExport,
        handleDownloadPng,
        handleDownloadSvg,
        buildExportSvgString,
        applyPreset,
        getPresetValues,
        resetParameters,
        undo,
        redo,
        canUndo,
        canRedo,
    } = usePixelStretch();

    const handleInt =
        (setter: (v: number) => void, min = 1, max = 8192) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n)) return;
            setter(Math.max(min, Math.min(max, Math.round(n))));
        };

    return (
        <div className="panel-controls pixel-stretch-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>Pixel Stretch</h2>
                    <ToolDescriptionPopover dialogLabel="Pixel Stretch tool description">
                        <p>
                            Load a raster image and place ordered crop
                            rectangles. Each pair of consecutive nodes is
                            connected by stretching the outgoing edge pixels of
                            the earlier node toward the incoming edge of the
                            next.
                        </p>
                        <p>
                            Drag on the canvas to add a node (crop matches the
                            drawn region). Select a node to move or resize its
                            frame on the artboard; the source crop stays fixed.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <UndoRedoControls
                        canUndo={canUndo}
                        canRedo={canRedo}
                        onUndo={undo}
                        onRedo={redo}
                    />
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                </div>
            </div>

            <div className="path-tool-inputs">
                <PresetSelector
                    toolId="pixel-stretch"
                    onApply={applyPreset}
                    onExport={getPresetValues}
                />

                <CollapsibleToolGroup title="Source image" icon={ImageIcon}>
                    <input
                        ref={imageInputRef}
                        type="file"
                        accept="image/*"
                        className="visually-hidden"
                        onChange={handleImageLoad}
                    />
                    <button
                        type="button"
                        className="tool-file-btn tool-file-btn--stretch"
                        onClick={() => imageInputRef.current?.click()}
                    >
                        <Upload size={16} aria-hidden />
                        {imageName ? "Replace image" : "Upload image"}
                    </button>
                    {imageName && (
                        <p className="tool-file-name">{imageName}</p>
                    )}
                    {imageLoadError && (
                        <p className="tool-error">{imageLoadError}</p>
                    )}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Tools" icon={MousePointer2}>
                    <div className="pixel-stretch-tool-modes">
                        <button
                            type="button"
                            className={`generic-button${toolMode === "addRect" ? " is-active" : ""}`}
                            onClick={() => setToolMode("addRect")}
                        >
                            <Square size={14} aria-hidden /> Add rect
                        </button>
                        <button
                            type="button"
                            className={`generic-button${toolMode === "select" ? " is-active" : ""}`}
                            onClick={() => setToolMode("select")}
                        >
                            <MousePointer2 size={14} aria-hidden /> Select
                        </button>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Nodes" icon={Square}>
                    {nodes.length === 0 ? (
                        <p className="tool-hint">
                            Add at least two rectangles to see connectors.
                        </p>
                    ) : (
                        <>
                            <ul className="pixel-stretch-node-list">
                                {nodes.map((node, index) => (
                                    <li
                                        key={node.id}
                                        className={`pixel-stretch-node-item${selectedNodeId === node.id ? " is-selected" : ""}`}
                                    >
                                        <button
                                            type="button"
                                            className="generic-button generic-button--icon"
                                            title="Select"
                                            onClick={() =>
                                                setSelectedNodeId(node.id)
                                            }
                                        >
                                            {index + 1}
                                        </button>
                                        <span className="pixel-stretch-node-item-label">
                                            {Math.round(node.frame.w)}x
                                            {Math.round(node.frame.h)} @ (
                                            {Math.round(node.frame.x)},{" "}
                                            {Math.round(node.frame.y)})
                                        </span>
                                        <button
                                            type="button"
                                            className="generic-button generic-button--icon"
                                            title="Move up"
                                            disabled={index === 0}
                                            onClick={() =>
                                                moveNodeUp(node.id)
                                            }
                                        >
                                            <ArrowUp size={14} aria-hidden />
                                        </button>
                                        <button
                                            type="button"
                                            className="generic-button generic-button--icon"
                                            title="Move down"
                                            disabled={
                                                index === nodes.length - 1
                                            }
                                            onClick={() =>
                                                moveNodeDown(node.id)
                                            }
                                        >
                                            <ArrowDown size={14} aria-hidden />
                                        </button>
                                        <button
                                            type="button"
                                            className="generic-button generic-button--icon"
                                            title="Delete"
                                            onClick={() =>
                                                removeNode(node.id)
                                            }
                                        >
                                            <Trash2 size={14} aria-hidden />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                            <button
                                type="button"
                                className="generic-button"
                                onClick={clearNodes}
                            >
                                Clear all nodes
                            </button>
                        </>
                    )}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Artboard" icon={Square}>
                    <label className="tool-field">
                        <span>Width (px)</span>
                        <DebouncedInput
                            type="number"
                            min={1}
                            max={8192}
                            value={artboardWidth}
                            onChange={handleInt(setArtboardWidth)}
                        />
                    </label>
                    <label className="tool-field">
                        <span>Height (px)</span>
                        <DebouncedInput
                            type="number"
                            min={1}
                            max={8192}
                            value={artboardHeight}
                            onChange={handleInt(setArtboardHeight)}
                        />
                    </label>
                    <label className="tool-field">
                        <span>Background</span>
                        <input
                            type="color"
                            value={backgroundColor}
                            onChange={(e) =>
                                setBackgroundColor(e.target.value)
                            }
                        />
                    </label>
                    <CheckboxField
                        label="Show dimmed source image"
                        checked={showSourceImage}
                        onChange={setShowSourceImage}
                    />
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Export" icon={Download} defaultOpen>
                    <label className="tool-field">
                        <span>Connector quality (max samples)</span>
                        <DebouncedInput
                            type="number"
                            min={8}
                            max={512}
                            value={connectorMaxSamples}
                            onChange={handleInt(setConnectorMaxSamples, 8, 512)}
                        />
                    </label>
                    <button
                        type="button"
                        className="generic-button"
                        disabled={!canExport}
                        onClick={handleDownloadPng}
                    >
                        <Download size={16} aria-hidden />
                        Download PNG
                    </button>
                    <button
                        type="button"
                        className="generic-button"
                        disabled={!canExport}
                        onClick={handleDownloadSvg}
                    >
                        <Download size={16} aria-hidden />
                        Download SVG
                    </button>
                    <SendSvgToToolMenu
                        disabled={!canExport}
                        getSvg={() => {
                            const content = buildExportSvgString();
                            return content
                                ? {
                                      name: "pixel-stretch-export.svg",
                                      content,
                                  }
                                : null;
                        }}
                        allowedSlots={["path", "object", "primary"]}
                        excludeTargetPanels={["pixel-stretch"]}
                    />
                </CollapsibleToolGroup>
            </div>
        </div>
    );
}
